import _ from 'underscore';
import ko from 'knockout';
import Backbone from 'backbone';
import ViewModel from '../viewmodel/index';
import template from '../template/index.pug';

export default class FilePicker extends Backbone.View {
  /**
   *
   * @param {object} options the options to build a filepicker instance.
   *   See the detail fields below.
   * @param {boolean} options.isNative will use the browser native button when it is set to true.
   * Otherwise will use the customized elements and hide the native ones.
   * @param {array} options.allowedFileExtensions the allowed file extensions. An array of strings.
   * Example: ['.txt', '.exe']. Will accept all file extensions if this field is not set.
   * @param {function} options.validator function to validate your file. The file object as
   * paramter. Return string for true is to raise an error. If you return true will use the
   * predefined error message.
   * @param {object} options.i18n object: contains getString function for i18n.
   * @param {function} options.i18n.getString pass a key(and model if needed) and returns a string.
   * @param {function} options.displayFilePath an enum of 'full' or 'filename', indicating the
   * display name of the chosen file in customized elements
   *
   * @event FilePicker#changed triggered when file is changed.
   * @event FilePicker#cleared triggered when file is cleared.
   * @event FilePicker#error.extension triggered when file extension is invalid.
   *
   * @returns {undefined} no return value
   *
   */
  initialize({
    isNative = false,
    allowedFileExtensions = [],
    validateFileExtensionsFailedMessage,
    validator = _.noop,
    i18n = {},
    displayFilePath = 'full',
    enableMultiple = false,
    useButton = false,
  }) {
    this.isNative = isNative;
    this.i18n = i18n;
    this.allowedFileExtensions = [];
    this.enableMultiple = enableMultiple;
    this.useButton = useButton;

    if (!_.isFunction(i18n.getString)) {
      this.i18n = { getString: _.identity };
    }

    if (!_.isEmpty(allowedFileExtensions)) {
      if (_.isArray(allowedFileExtensions)) {
        this.allowedFileExtensions = allowedFileExtensions;
      } else {
        throw new Error('allowedFileExtensions accepts array of strings only.');
      }
    }

    this.viewModel = new ViewModel({
      i18n: this.i18n,
      validator,
      allowedFileExtensions: this.allowedFileExtensions,
      validateFileExtensionsFailedMessage,
      getFileObject: () => this.fileObject,
      getFileObjects: () => this.fileObjects,
      trigger: this.trigger.bind(this),
      displayFilePath,
      enableMultiple: this.enableMultiple,
    });

    this.fileInputCssId = _.uniqueId('el_fileinput_');
  }

  render() {
    this.$el.html(template({
      fileInputCssId: this.fileInputCssId,
      i18n: this.i18n,
      isNative: this.isNative,
      acceptString: this.allowedFileExtensions.join(),
      enableMultiple: this.enableMultiple,
      useButton: this.useButton,
    }));
    ko.applyBindings(this.viewModel, this.el);
    return this;
  }

  get events() {
    const { useButton } = this;
    const buttonEvent = useButton ? { 'click .file-picker-btn': 'triggerFileInputClick' } : {};

    return {
      'click .remove-file': 'removeFile',
      ...buttonEvent,
    };
  }

  /**
   * @returns {string} the full path file
   */
  get filePath() {
    return this.viewModel.filePath();
  }

  /**
   * @returns {Object} the object to describe the file. Refer https://developer.mozilla.org/en-US/docs/Web/API/File for more details.
   * @example
   * {
   *    lastModified: 1465909575599,
   *    lastModifiedDate: Tue Jun 14 2016 21:06:15 GMT+0800 (China Standard Time),
   *    name: 'test.txt',
   *    size: 3,
   *    type: "",
   *    webkitRelativePath: ''
   * }
   */
  get fileObject() {
    return _.first(this.fileObjects);
  }

  get fileObjects() {
    return _.first(this.$(`#${this.fileInputCssId}`)).files;
  }

  removeFile() {
    this.viewModel.filePath('');
  }

  triggerFileInputClick() {
    this.$(`#${this.fileInputCssId}`).trigger('click');
  }

  addExternalValidationErrors(msg) {
    this.viewModel.errors.push(msg);
  }

  hasErrors() {
    return this.viewModel.hasErrors();
  }
}
